<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { searchNominatim, type NominatimResult } from '@api/nominatim';

interface Props {
  modelValue: string;
  placeholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Введите адрес'
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  locationUpdate: [location: { latitude: number; longitude: number; country: string; city: string }];
}>();

const addressInput = ref<HTMLInputElement>();
const suggestionsContainer = ref<HTMLDivElement>();
const suggestions = ref<NominatimResult[]>([]);
const showSuggestions = ref(false);
const selectedIndex = ref(-1);
const isLoading = ref(false);
const inputValue = ref(props.modelValue || '');

// Debounce function to limit API calls
let debounceTimer: number;

onMounted(() => {
  // Initialize input value
  inputValue.value = props.modelValue || '';

  // Focus on input when component mounts
  if (addressInput.value) {
    addressInput.value.focus();
  }
});

const searchAddresses = async (query: string) => {
  if (!query || query.length < 3) {
    suggestions.value = [];
    showSuggestions.value = false;
    return;
  }

  isLoading.value = true;
  showSuggestions.value = true;

  try {
    suggestions.value = await searchNominatim(query);
    selectedIndex.value = -1;
  } catch (error) {
    // Keep UI usable even if external geocoding is down/blocked.
    suggestions.value = [];
  } finally {
    isLoading.value = false;
  }
};

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = target.value;
  inputValue.value = value;

  // For manual input, emit the full value as the address
  // This allows users to type full addresses manually
  emit('update:modelValue', value);

  // Clear previous timer
  clearTimeout(debounceTimer);

  // Set new timer for debounced search
  debounceTimer = setTimeout(() => {
    searchAddresses(value);
  }, 300);
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    selectedIndex.value = Math.min(selectedIndex.value + 1, suggestions.value.length - 1);
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    selectedIndex.value = Math.max(selectedIndex.value - 1, -1);
  } else if (event.key === 'Enter') {
    event.preventDefault();
    if (selectedIndex.value >= 0 && suggestions.value[selectedIndex.value]) {
      selectSuggestion(suggestions.value[selectedIndex.value]);
    }
  } else if (event.key === 'Escape') {
    showSuggestions.value = false;
    selectedIndex.value = -1;
  }
};

// Helper function to extract city from Russian address
const extractRussianCity = (address: any, fullAddress: string): string => {
  // For Russian addresses, prioritize state over municipality when municipality contains "округ"
  if (address.state &&
      address.municipality &&
      address.municipality.includes('округ')) {
    // If municipality is a district (округ), use state as the city
    return address.state;
  }

  // First try the structured address fields, but be more careful about state_district
  let structuredCity = address.city ||
                      address.town ||
                      address.village ||
                      '';

  // For municipality, only use it if it doesn't contain "округ" or "район"
  if (address.municipality &&
      !address.municipality.includes('округ') &&
      !address.municipality.includes('район')) {
    structuredCity = structuredCity || address.municipality;
  }

  // For state_district, only use it if it doesn't contain "округ" or "район"
  if (address.state_district &&
      !address.state_district.includes('округ') &&
      !address.state_district.includes('район')) {
    structuredCity = structuredCity || address.state_district;
  }

  // For state, only use it if it looks like a city name
  if (address.state &&
      !address.state.includes('область') &&
      !address.state.includes('край') &&
      !address.state.includes('республика')) {
    structuredCity = structuredCity || address.state;
  }

  if (structuredCity) {
    return structuredCity;
  }

  // If no structured city found, parse the display_name
  const addressParts = fullAddress.split(', ');

  // Major Russian cities that should be prioritized
  const majorRussianCities = [
    'Санкт-Петербург', 'Москва', 'Новосибирск', 'Екатеринбург', 'Казань',
    'Нижний Новгород', 'Челябинск', 'Самара', 'Уфа', 'Ростов-на-Дону',
    'Краснодар', 'Пермь', 'Воронеж', 'Волгоград', 'Красноярск',
    'Саратов', 'Тюмень', 'Тольятти', 'Ижевск', 'Барнаул', 'Ульяновск',
    'Иркутск', 'Хабаровск', 'Ярославль', 'Владивосток', 'Махачкала',
    'Томск', 'Оренбург', 'Кемерово', 'Новокузнецк', 'Рязань', 'Астрахань',
    'Набережные Челны', 'Пенза', 'Липецк', 'Киров', 'Чебоксары', 'Тула',
    'Калининград', 'Курск', 'Улан-Удэ', 'Ставрополь', 'Магнитогорск',
    'Сочи', 'Брянск', 'Иваново', 'Тверь', 'Белгород', 'Архангельск',
    'Севастополь', 'Симферополь'
  ];

  // Look for major cities first
  for (const part of addressParts) {
    if (majorRussianCities.includes(part)) {
      return part;
    }
  }

  // Look for patterns that indicate a city (ending with "град", "ск", etc.)
  for (const part of addressParts) {
    if (part.endsWith('град') ||
        part.endsWith('ск') ||
        part.endsWith('ов') ||
        part.endsWith('ин') ||
        part.endsWith('ец') ||
        part.includes('город')) {
      return part;
    }
  }

  // If still no city found, try to find the most likely city candidate
  // Usually the city is in the middle part of the address, not at the beginning or end
  for (let i = 1; i < addressParts.length - 1; i++) {
    const part = addressParts[i];
    // Skip parts that are clearly not cities
    if (!part.includes('улица') &&
        !part.includes('проспект') &&
        !part.includes('переулок') &&
        !part.includes('дом') &&
        !part.includes('квартира') &&
        !part.includes('округ') &&
        !part.includes('район') &&
        !part.includes('область') &&
        !part.includes('край') &&
        !part.includes('республика') &&
        part.length > 3) {
      return part;
    }
  }

  return '';
};

const selectSuggestion = (suggestion: NominatimResult) => {
  const fullAddress = suggestion.display_name;
  inputValue.value = fullAddress;

  // Extract address components for location data
  const address = suggestion.address;
  let country = '';
  let city = '';

  if (address) {
    // Get country code and normalize to lowercase
    country = address.country_code || '';
    const normalizedCountry = country.toLowerCase();

    // Extract city with priority order for different address formats
    // For Russian addresses and other complex formats, we need to be more specific
    if (normalizedCountry === 'ru') {
      try {
        city = extractRussianCity(address, fullAddress);
      } catch (error) {
        console.error('Error in extractRussianCity:', error);
        // Fallback to old logic
        city = address.city ||
               address.town ||
               address.village ||
               address.municipality ||
               address.state ||
               '';
      }
    } else {
      // For other countries, try standard fields
      city = address.city ||
             address.town ||
             address.village ||
             address.municipality ||
             address.locality ||
             address.suburb ||
             address.district ||
             address.state ||
             address.county ||
             '';
    }
  }

  // Emit the full address for the address field
  emit('update:modelValue', fullAddress);

  emit('locationUpdate', {
    latitude: parseFloat(suggestion.lat),
    longitude: parseFloat(suggestion.lon),
    country,
    city
  });

  showSuggestions.value = false;
  selectedIndex.value = -1;
};

const handleFocus = () => {
  if (suggestions.value.length > 0 || isLoading.value) {
    showSuggestions.value = true;
  }
};

const handleBlur = () => {
  // Delay hiding suggestions to allow for clicks
  setTimeout(() => {
    showSuggestions.value = false;
    selectedIndex.value = -1;
  }, 200);
};

// Watch for modelValue changes to update input value
watch(() => props.modelValue, (newValue) => {
  if (newValue !== inputValue.value) {
    inputValue.value = newValue || '';
  }
});
</script>

<template>
  <div class="address-input-wrapper">
    <!-- Main address input -->
    <div class="input-container">
      <i class="bi bi-geo-alt address-icon"></i>
      <input
        ref="addressInput"
        :value="inputValue"
        @input="handleInput"
        @keydown="handleKeyDown"
        @focus="handleFocus"
        @blur="handleBlur"
        :placeholder="placeholder"
        class="address-input"
        type="text"
        autocomplete="off"
        data-1p-ignore
      />
    </div>

    <!-- Loading indicator -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-content">
        <i class="bi bi-arrow-clockwise loading-spinner"></i>
        <span>Поиск адресов...</span>
      </div>
    </div>

    <!-- Address suggestions dropdown -->
    <div
      v-else-if="showSuggestions && suggestions.length > 0"
      ref="suggestionsContainer"
      class="suggestions-container"
    >
      <div
        v-for="(suggestion, index) in suggestions"
        :key="suggestion.place_id"
        :class="['suggestion-item', { 'selected': index === selectedIndex }]"
        @mousedown.prevent="selectSuggestion(suggestion)"
      >
        <i class="bi bi-geo-alt suggestion-icon"></i>
        <div class="suggestion-content">
          <div class="suggestion-main">{{ suggestion.display_name.split(',')[0] }}</div>
          <div class="suggestion-details">{{ suggestion.display_name.split(',').slice(1).join(',') }}</div>
        </div>
      </div>
    </div>

    <!-- No results message -->
    <div v-else-if="showSuggestions && suggestions.length === 0 && !isLoading && inputValue.length >= 3" class="no-results">
      <i class="bi bi-exclamation-circle"></i>
      Адрес не найден. Попробуйте изменить запрос.
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Color Variables
$primary-bg: #EFD8B0;
$primary-text: #BD9C65;
$icon-bg: #C29C58;
$icon-text: #60451F;
$hover-bg: #B08F4F;
$border-color: #BD9C65;
$focus-shadow: rgba(189, 156, 101, 0.2);

.address-input-wrapper {
  position: relative;
  width: 100%;
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.address-icon {
  position: absolute;
  left: 12px;
  color: $icon-text;
  font-size: 16px;
  z-index: 2;
  pointer-events: none;
}

.address-input {
  width: 100%;
  background-color: $primary-bg !important;
  color: $primary-text !important;
  border: 1px solid $border-color;
  border-radius: 4px;
  padding: 8px 12px 8px 40px;
  font-size: 14px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: text;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: $border-color;
    box-shadow: 0 0 0 2px $focus-shadow;
  }

  &::placeholder {
    color: $primary-text;
    opacity: 0.7;
  }
}

.loading-container {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: $primary-bg;
  border: 1px solid $border-color;
  border-top: none;
  border-radius: 0 0 4px 4px;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.loading-content {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  color: $primary-text;
  font-size: 14px;
}

.loading-spinner {
  color: $icon-text;
  font-size: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.suggestions-container {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: $primary-bg;
  border: 1px solid $border-color;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.suggestion-item {
  display: flex;
  align-items: flex-start;
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid rgba($border-color, 0.2);

  &:last-child {
    border-bottom: none;
  }

  &:hover,
  &.selected {
    background-color: $icon-bg;
    color: $icon-text;
  }

  .suggestion-icon {
    margin-right: 8px;
    font-size: 14px;
    color: inherit;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .suggestion-content {
    flex: 1;
    min-width: 0;
  }

  .suggestion-main {
    font-weight: 500;
    margin-bottom: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .suggestion-details {
    font-size: 12px;
    opacity: 0.8;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.no-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: $primary-bg;
  border: 1px solid $border-color;
  border-top: none;
  border-radius: 0 0 4px 4px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: $primary-text;
  z-index: 1000;

  .bi {
    color: $icon-text;
    font-size: 16px;
  }
}
</style>
